import React, { useEffect, useState, ReactNode } from 'react'

import {
  Alert,
  Col,
  Container,
  Row
} from 'react-bootstrap'
import Helmet from 'react-helmet'
import { Link, useParams } from 'react-router-dom'

import { AuthorsList } from '../common/AuthorsList'
import { getCitation, getSchema } from '../services/LandingPage'
import { Citations } from './components/Citations'
import { Downloads } from './components/Downloads'
import { GeolocationMap } from './components/GeolocationMap'
import { Matomo } from './components/Matomo'
import { RisLink } from './components/RisLink'
import { Citation } from './dataStructures'

const dayjs = require('dayjs')

const TableRow = ({ title, children }: {title: string, children: ReactNode}) => {
  return (
    <tr>
      <th>{title}</th>
      <td>{children}</td>
    </tr>
  )
}
export const LandingPage = () => {
  const { uuid = '' } = useParams()
  const [loadingError, setLoadingError] = useState<string>()
  const [data, setData] = useState<Citation | null>()
  const [schema, setSchema] = useState<any | null>()
  const [embargoDate, setEmbargoDate] = useState<Date | null>(null)
  const secondsToWait = 120
  const loadData = async () => {
    try {
      const results = await getCitation(uuid)
      document.getElementsByTagName('title')[0].innerHTML = results.shortTitle
      setData(results)
      if (results.endEmbargo) {
        setEmbargoDate(new Date(new Date(`${results.endEmbargo}`).getTime() + secondsToWait * 1000))
      }
    } catch (ex) {
      setLoadingError(ex instanceof Error ? ex.message : 'Unknown error.')
    }
  }
  useEffect(() => { loadData() }, [uuid])

  const loadSchema = async () => {
    try {
      const results = await getSchema(uuid)
      setSchema(results)
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.innerHTML = JSON.stringify(results)
      document.head.appendChild(script)
    } catch (ex) {
      setLoadingError(ex instanceof Error ? ex.message : 'Unknown error.')
    }
  }
  useEffect(() => { loadSchema() }, [uuid])

  if (!data && !loadingError) return null

  return (
    <Container className='landing-page'>
      <Helmet>
        <link rel="profile" href="http://dublincore.org/specifications/dublin-core/dcq-html/" />
        <link rel="schema.DC" href="http://purl.org/dc/elements/1.1/"></link>
        <link rel="schema.DCTERMS" href="http://purl.org/dc/terms/"></link>
        <meta name="DC.title" content={data?.shortTitle}></meta>
        <meta name="DC.subject" content={data?.subjects.join(',')}></meta>
        <meta name="DC.creator" content={data?.authors[0].name}></meta>
        <meta name="DC.publisher" content={data?.publisher}></meta>
        <meta name="DC.coverage" content={data?.spatialCoverage.join(',')}></meta>
        <meta name="DC.format" content={data?.fileFormats.join(',')}></meta>
        <meta name="DC.language" content={data?.languages.join(',')}></meta>
        <meta name="DC.date" content={dayjs(data?.publicationDate!).format('YYYY-MM-DD') }></meta>
        <meta
          name="DC.period"
          content={
            'start=' + dayjs(data?.temporalCoverage.start!).format('YYYY-MM-DD') + '; ' +
            'end=' + dayjs(data?.temporalCoverage.end!).format('YYYY-MM-DD') + ' scheme=W3C-DTF'
          }
        ></meta>
        {schema !== null ? <meta name="DC.identifier" content={schema?.identifier.url} ></meta> : null}
        {schema !== null ? <meta name="DC.description" content={schema?.description} ></meta> : null}
        {data?.licence && <meta name="DCTERMS.license" content={data?.licence.url}></meta>}
        {data?.authors.map((author, index) => {
          if (index === 0) { return null }
          return <meta key={index} name="DC.contributor" content={author.name} />
        })}
      </Helmet>

      {loadingError && <Alert variant="danger">{loadingError}</Alert>}

      {data &&
        <>
          <h1 dangerouslySetInnerHTML={{ __html: data.shortTitle }}></h1>
          {data.isMostRecent
            ? <Alert variant='primary'>
                This is the most recent published version of these data held at BODC.
              </Alert>
            : <Alert variant='warning'>
                The latest version of the dataset may be accessed here{' '}
                <Link to={`/catalogue/${data.replacedBy[0].doi}`}>{data.replacedBy[0].title}</Link>
              </Alert>
          }

          {data.licence
            ? <p>These data are made available under <a href={data.licence.url}>{data.licence.name}</a>.</p>
            : <p>Licence unavailable.</p>
          }
          <Row style={{ paddingTop: '20px', paddingBottom: '20px' }}>
            <Col>
              {(embargoDate == null) || (embargoDate < new Date())
                ? <Downloads uuid={uuid} downloadLinks={data.downloadLinks}/>
                : <p>These data are under an embargo until {data.endEmbargo}. Please <a href='https://www.bodc.ac.uk/about/contact_us/contact_details/'>contact us</a> should you wish to gain access to these data.</p>
              }
            </Col>
            <Col style={{ textAlign: 'right' }}>
              <Citations uuid={uuid} />
              <br />
              <Matomo uuid={uuid} />
              <br />
              <RisLink uuid={uuid} />
            </Col>
          </Row>

          <table>
            <tbody>
              <TableRow title='Title' >
                <p dangerouslySetInnerHTML={{ __html: data.title }} />
              </TableRow>
              <TableRow title='Author(s)'>
                <AuthorsList authors={data.authors} organisations={data.authorOrganisations} />
              </TableRow>
              {data.authorOrganisations.length > 0 &&
                <TableRow title='Organisation(s)'>
                  {data.authorOrganisations.map((x) => `(${x.displayIndex}) ${x.name}`).join('; ')}
                </TableRow>
              }
              {data.contributors.length > 0 &&
                <TableRow title='Contributor(s)'>
                  <AuthorsList authors={data.contributors} organisations={data.contributorOrganisations} />
                </TableRow>
              }
              {data.contributorOrganisations.length > 0 &&
                  <TableRow title='Organisation(s)'>
                    {data.contributorOrganisations.map((x) => `(${x.displayIndex}) ${x.name}`).join('; ')}
                  </TableRow>
              }
              <TableRow title='Subject'>
                {data.subjects.join(', ')}
              </TableRow>
              <TableRow title='Digital object identifier (DOI)'>
                {data.doi}
              </TableRow>
              <TableRow title='Abstract'>
                <p dangerouslySetInnerHTML={{ __html: data.abstract }} />
              </TableRow>
              <TableRow title="Citation">
                <p dangerouslySetInnerHTML={{ __html: data.citation }} />
              </TableRow>
              <TableRow title='Temporal Coverage'>
                {`${data.temporalCoverage.start.format('DD MMMM YYYY')} - ${data.temporalCoverage.end.format('DD MMMM YYYY')}`}
              </TableRow>
              <TableRow title='Spatial coverage'>
                {data.spatialCoverage.join(', ')}
                {data.geolocations.length > 0 &&
                  <GeolocationMap boundingBoxes={data.geolocations}/>
                }
              </TableRow>
              <TableRow title='File formats'>
                {data.fileFormats.join(', ')}
              </TableRow>
              <TableRow title='Languages'>
                {data.languages.join(', ')}
              </TableRow>
              <TableRow title='Discovery metadata record'>
                <a href={`${process.env.REACT_APP_WEB_HOST}/data/information_and_inventories/edmed/report/${data.edmed.id}/`}>
                  {data.edmed.title}
                </a>
              </TableRow>
              <TableRow title='Publisher'>
                {data.publisher}
              </TableRow>
              <TableRow title='Publication date'>
                {data.publicationDate.format('DD MMMM YYYY')}
              </TableRow>
              {data.replaces.length > 0 &&
                <TableRow title='Older versions of this data'>
                  {data.replaces.map(x =>
                    <p key={x.doi}>
                      <Link to={`/catalogue/${x.doi}`}>{x.title}</Link>
                    </p>
                  )}
                </TableRow>
              }
              {data.replacedBy.length > 0 &&
                <TableRow title='Newer versions of this data'>
                  {data.replacedBy.map(x =>
                    <p key={x.doi}>
                      <Link to={`/catalogue/${x.doi}`}>{x.title}</Link>
                    </p>
                  )}
                </TableRow>
              }
            </tbody>
          </table>
        </>
      }
    </Container>
  )
}
